import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@mui/material';

import { getShortAreaUnit } from '../../../../../shared/features/areaUnit/helpers/functions/areaUnit';
import CalculatorTable from '../../../../components/CalculatorTable';
import rootStore from '../../../rootStore/RootStore';
import usePriceConfig from '../../../../../shared/features/priceConfig/hooks/usePriceConfig';
import { getCurrencyForAreaUnit } from '../../../../../shared/helpers/functions/currency';
import { MAX_AREA_AMOUNTS } from '../../../../../shared/helpers/constants/areaAmounts';

const MonthlyCostsCalculatorTable = observer(() => {
  const { t } = useTranslation();
  const { config } = usePriceConfig(rootStore);
  const { uiStateStore, areaUnitStore } = rootStore;
  const { monthlyPlan } = uiStateStore;
  const { areaUnit } = areaUnitStore;

  const currency = getCurrencyForAreaUnit(areaUnit);
  const shortAreaUnit = getShortAreaUnit(areaUnit);
  const area = parseFloat(monthlyPlan.area);

  const monthlyFee =
    config?.MonthlyPayAsYouGo.BaseMonthlyFee?.prices[currency]?.price ?? 0;
  const fieldStorageCost =
    config?.MonthlyPayAsYouGo.FieldStorage?.prices[currency]?.price ?? 0;

  const maxVolume = MAX_AREA_AMOUNTS[areaUnit];
  const maxVolumeExceeded = area && maxVolume && area > maxVolume;

  const rows = [
    {
      title: t('plan-select.pricing-example.rows.core-features'),
      quantity: 1,
      quantityUnit: '',
      price: monthlyFee,
      amount: monthlyFee,
    },
    {
      title: t('plan-select.pricing-example.rows.field-monitoring-storage'),
      quantity: area || 0,
      quantityUnit: shortAreaUnit,
      price: maxVolumeExceeded ? null : fieldStorageCost,
      amount: (area ?? 0) * fieldStorageCost,
    },
  ];

  const totalAmount = maxVolumeExceeded
    ? null
    : rows.reduce((acc, row) => acc + (row.amount || 0), 0);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {t('plans.monthly.pricing-example.subsequent-monthly-costs')}
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 'normal' }}>
          {t('plans.monthly.pricing-example.subsequent-month-description')}
        </Typography>
      </Box>
      <CalculatorTable
        areaUnit={areaUnit}
        rows={rows}
        total={{
          title: t('plan-select.pricing-example.total'),
          amount: totalAmount,
        }}
      />
    </Box>
  );
});

export default MonthlyCostsCalculatorTable;
